import React, { useState, useEffect, useCallback } from 'react';
import {
  getAllUsers,
  getUserDetails,
  getUserWatches,
  getUserLocks,
  updateUserDetails,
  resetUserPassword,
  getPasswordResetLogs,
  getUserSigninLogs
} from '../adminApi';
import styles from '../styles/UserManagement.module.css';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [userDetails, setUserDetails] = useState(null);
  const [userWatches, setUserWatches] = useState([]);
  const [userLocks, setUserLocks] = useState([]);
  const [passwordResetLogs, setPasswordResetLogs] = useState([]);
  const [signinLogs, setSigninLogs] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUserData = useCallback(async () => {
    setLoading(true);
    try {
      const [details, watches, locks, resetLogs, signinLogs] = await Promise.all([
        getUserDetails(selectedUser),
        getUserWatches(selectedUser, selectedYear),
        getUserLocks(selectedUser, selectedYear),
        getPasswordResetLogs({ user_id: selectedUser, limit: 10 }),
        getUserSigninLogs({ user_id: selectedUser, limit: 10 })
      ]);
      setUserDetails(details.data);
      setUserWatches(watches.data);
      setUserLocks(locks.data);
      setPasswordResetLogs(resetLogs.data);
      setSigninLogs(signinLogs.data);
    } catch (err) {
      setError('Failed to fetch user data');
    } finally {
      setLoading(false);
    }
  }, [selectedUser, selectedYear]);

  useEffect(() => {
    if (selectedUser) {
      fetchUserData();
    }
  }, [selectedUser, selectedYear, fetchUserData]);

  const fetchUsers = async () => {
    try {
      const response = await getAllUsers();
      setUsers(response.data);
    } catch (err) {
      setError('Failed to fetch users');
    }
  };

  const handleUpdateField = async (field, value) => {
    try {
      await updateUserDetails(selectedUser, { [field]: value });
      setUserDetails(prev => ({ ...prev, [field]: value }));
    } catch (err) {
      setError(`Failed to update ${field}`);
    }
  };

  const handlePasswordReset = async () => {
    try {
      await resetUserPassword(selectedUser, newPassword);
      setNewPassword('');
      setError('Password reset successful');
    } catch (err) {
      setError('Failed to reset password');
    }
  };

  const renderYearSelector = () => (
    <div className={styles.yearSelector}>
      <label htmlFor="yearSelect">Select Year:</label>
      <select
        id="yearSelect"
        value={selectedYear}
        onChange={(e) => setSelectedYear(Number(e.target.value))}
        className={styles.select}
      >
        {[...Array(5)].map((_, i) => {
          const year = new Date().getFullYear() - i;
          return (
            <option key={`year-${year}`} value={year}>
              {year}
            </option>
          );
        })}
      </select>
    </div>
  );

  const renderWatchesTable = () => (
    <div className={styles.section}>
      <h4 className={styles.sectionTitle}>User Watches</h4>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Week</th>
            <th>Game ID</th>
            <th>Pick Type</th>
            <th>Watch Line</th>
            <th>Created At</th>
          </tr>
        </thead>
        <tbody>
          {userWatches.map((watch) => (
            <tr key={`watch-${watch.game_id}-${watch.pick_type}-${watch.created_at}`}>
              <td>{watch.week}</td>
              <td>{watch.game_id}</td>
              <td>{watch.pick_type}</td>
              <td>{watch.watch_line}</td>
              <td>{new Date(watch.created_at).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderLocksTable = () => (
    <div className={styles.section}>
      <h4 className={styles.sectionTitle}>User Locks</h4>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Week</th>
            <th>Game ID</th>
            <th>Pick Type</th>
            <th>Lock Line</th>
            <th>Status</th>
            <th>Created At</th>
          </tr>
        </thead>
        <tbody>
          {userLocks.map((lock) => (
            <tr key={`lock-${lock.game_id}-${lock.pick_type}-${lock.created_at}`}>
              <td>{lock.week}</td>
              <td>{lock.game_id}</td>
              <td>{lock.pick_type}</td>
              <td>{lock.lock_line}</td>
              <td>{lock.status}</td>
              <td>{new Date(lock.created_at).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderPasswordResetLogsTable = () => (
    <div className={styles.section}>
      <h4 className={styles.sectionTitle}>Password Reset Activity</h4>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Action</th>
            <th>Channel</th>
            <th>Success</th>
            <th>IP Address</th>
            <th>Created At</th>
          </tr>
        </thead>
        <tbody>
          {passwordResetLogs.map(log => (
            <tr key={log.id} className={log.success ? styles.successRow : styles.errorRow}>
              <td>{log.action}</td>
              <td>{log.channel}</td>
              <td>{log.success ? '✓' : '✗'}</td>
              <td>{log.ip_address || 'N/A'}</td>
              <td>{new Date(log.created_at).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderSigninLogsTable = () => (
    <div className={styles.section}>
      <h4 className={styles.sectionTitle}>Sign-in Activity</h4>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Success</th>
            <th>IP Address</th>
            <th>User Agent</th>
            <th>Created At</th>
          </tr>
        </thead>
        <tbody>
          {signinLogs.map(log => (
            <tr key={log.id} className={log.success ? styles.successRow : styles.errorRow}>
              <td>{log.success ? '✓' : '✗'}</td>
              <td>{log.ip_address || 'N/A'}</td>
              <td title={log.user_agent}>
                {log.user_agent ? log.user_agent.substring(0, 50) + '...' : 'N/A'}
              </td>
              <td>{new Date(log.created_at).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className={styles.userManagement}>
      <h3 className={styles.title}>User Management</h3>
      {error && <p className={styles.error}>{error}</p>}

      <div className={styles.userSelector}>
        <label htmlFor="userSelect">Select a user:</label>
        <select
          id="userSelect"
          value={selectedUser || ''}
          onChange={(e) => setSelectedUser(e.target.value)}
          className={styles.select}
        >
          <option value="">Select a user</option>
          {users.map(user => (
            <option key={user.id} value={user.id}>
              {user.username}
            </option>
          ))}
        </select>
      </div>

      {selectedUser && !loading && userDetails && (
        <>
          <div className={styles.userDetailsPanel}>
            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>User Information</h4>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Active Status</label>
                <input
                  type="checkbox"
                  checked={!userDetails.inactive}
                  onChange={(e) => handleUpdateField('inactive', !e.target.checked)}
                  className={styles.checkbox}
                />
                <span className={styles.checkboxLabel}>
                  {userDetails.inactive ? 'Inactive' : 'Active'}
                </span>
              </div>
              {Object.entries({
                username: 'Username',
                email: 'Email',
                discord_username: 'Discord Username',
                role: 'Role',
                timezone: 'Timezone',
                phone: 'Phone'
              }).map(([field, label]) => (
                <div key={field} className={styles.field}>
                  <label className={styles.fieldLabel}>{label}</label>
                  <input
                    type="text"
                    value={userDetails[field] || ''}
                    onChange={(e) => handleUpdateField(field, e.target.value)}
                    className={styles.input}
                  />
                </div>
              ))}
            </div>

            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>Password Reset</h4>
              <div className={styles.field}>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="New Password"
                  className={styles.input}
                />
                <button
                  onClick={handlePasswordReset}
                  className={styles.button}
                >
                  Reset Password
                </button>
              </div>
            </div>
          </div>

          {renderYearSelector()}
          {userWatches.length > 0 && renderWatchesTable()}
          {userLocks.length > 0 && renderLocksTable()}
          {passwordResetLogs.length > 0 && renderPasswordResetLogsTable()}
          {signinLogs.length > 0 && renderSigninLogsTable()}
        </>
      )}
    </div>
  );
};

export default UserManagement;
